import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Swal from 'sweetalert2';
import logo from '../assets/logo-centrao.png';
import { apiPublica } from '../services/api';

// Pagina PUBLICA (sem login) — exibe a chave PIX gerada pelo sistema-suc.
// Consome o endpoint /pix/:payload do nosso backend (que delega ao Supabase).
// Antes o frontend chamava o Supabase direto; mover pro backend evita expor
// a anon key no bundle e centraliza rate-limit + auditoria de IP.

const URL_RETORNO = 'https://linktr.ee/contatocentrao';

function formatarMoeda(valor) {
  if (valor == null) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
}

function formatarExpiracao(timestampMs) {
  return new Intl.DateTimeFormat('pt-br', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(parseInt(timestampMs, 10));
}

export default function Pix() {
  const [searchParams] = useSearchParams();
  const payload = searchParams.get('payload');

  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [expiracaoTexto, setExpiracaoTexto] = useState(null);
  const [redirecionando, setRedirecionando] = useState(false);
  const [contador, setContador] = useState(10);
  const intervaloRedirectRef = useRef(null);

  // Carrega a chave PIX. Retorna `true` se encontrou e esta valida, `false` em
  // qualquer outro caso (expirou, nao encontrado, erro de rede).
  const carregarDados = useCallback(async () => {
    if (!payload) return false;
    try {
      const { data } = await apiPublica.get(`/pix/${encodeURIComponent(payload)}`);
      if (data.expirou) {
        setExpiracaoTexto('Chave PIX expirou');
        return false;
      }
      setDados({
        brcode: data.brcode,
        valor: data.valor,
        status: data.status,
        numped: data.numped,
        nomeCliente: data.nomeCliente,
        nomeVendedor: data.nomeVendedor,
      });
      if (data.status === 'Pagamento efetuado!') {
        setExpiracaoTexto(null);
      } else {
        setExpiracaoTexto('Expira em ' + formatarExpiracao(data.expiracao));
      }
      return true;
    } catch (_) {
      return false;
    }
  }, [payload]);

  // Boot: sem payload vai direto pro linktree; com payload busca os dados.
  useEffect(() => {
    if (!payload) {
      window.open(URL_RETORNO, '_self', 'noopener');
      return;
    }
    let cancelado = false;
    (async () => {
      const ok = await carregarDados();
      if (cancelado) return;
      setCarregando(false);
      if (!ok && intervaloRedirectRef.current == null) {
        setRedirecionando(true);
        intervaloRedirectRef.current = setInterval(() => {
          setContador((prev) => {
            if (prev <= 1) {
              clearInterval(intervaloRedirectRef.current);
              intervaloRedirectRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    })();
    return () => {
      cancelado = true;
      if (intervaloRedirectRef.current != null) {
        clearInterval(intervaloRedirectRef.current);
      }
    };
  }, [payload, carregarDados]);

  // Quando contador chega a zero, redireciona.
  useEffect(() => {
    if (contador <= 0 && redirecionando) {
      window.open(URL_RETORNO, '_self', 'noopener');
    }
  }, [contador, redirecionando]);

  // Polling: enquanto status for 'Pendente', refaz a busca a cada 5s.
  useEffect(() => {
    if (dados?.status !== 'Pendente' || !payload) return undefined;
    const id = setInterval(() => { carregarDados(); }, 5000);
    return () => clearInterval(id);
  }, [dados?.status, payload, carregarDados]);

  const copiarChave = async () => {
    if (!dados) return;
    if (dados.status !== 'Pendente') {
      Swal.fire({
        icon: dados.status === 'Pagamento efetuado!' ? 'info' : 'warning',
        title: dados.status === 'Pagamento efetuado!'
          ? 'Pagamento já efetuado'
          : 'Chave indisponível',
        text: dados.status === 'Pagamento efetuado!'
          ? 'Não é necessário copiar a chave — o pagamento já foi confirmado.'
          : 'Esta chave PIX expirou ou foi cancelada.',
        confirmButtonColor: '#2e9846',
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(dados.brcode);
      Swal.fire({
        icon: 'success',
        title: 'Chave copiada!',
        text: 'Cole no seu app do banco para concluir o pagamento.',
        confirmButtonColor: '#2e9846',
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (_) {
      Swal.fire({
        icon: 'error',
        title: 'Não foi possível copiar',
        text: 'Selecione a chave manualmente e copie pelo navegador.',
        confirmButtonColor: '#2e9846',
      });
    }
  };

  const status = dados?.status;
  const corStatus = status === 'Pagamento efetuado!' ? 'pago'
    : status === 'Pendente' ? 'pendente'
    : 'erro';
  const iconeStatus = status === 'Pagamento efetuado!' ? 'bi-check-circle-fill'
    : status === 'Pendente' ? 'bi-hourglass-split'
    : 'bi-x-circle-fill';

  return (
    <div className="pix-wrap-cli">
      <header className="pix-header-cli">
        <img src={logo} alt="Centrão Materiais" className="pix-logo-cli" />
        <div>
          <h1 className="pix-titulo-cli">Pagamento via PIX</h1>
          <p className="pix-subtitulo-cli">Centrão Materiais de Construção</p>
        </div>
      </header>

      <main className="pix-main-cli">
        {carregando && (
          <div className="pix-loader-cli">
            <div className="publico-spinner-cli" aria-hidden="true" />
            <p>Carregando dados do pagamento…</p>
          </div>
        )}

        {!carregando && !dados && (
          <div className="pix-card-cli pix-erro-cli">
            <i className="bi bi-exclamation-octagon pix-erro-icone-cli" />
            <h2>{expiracaoTexto || 'Chave PIX indisponível'}</h2>
            <p>Solicite uma nova chave ao seu vendedor para concluir o pagamento.</p>
            {redirecionando && (
              <small>Redirecionando para o site da Centrão em {contador}s…</small>
            )}
          </div>
        )}

        {!carregando && dados && (
          <div className="pix-card-cli">
            <div className={`pix-status-cli pix-status-${corStatus}-cli`}>
              <i className={`bi ${iconeStatus}`} />
              <span>{status}</span>
            </div>

            {dados.valor != null && (
              <div className="pix-valor-cli">
                <span className="pix-valor-label-cli">Valor a pagar</span>
                <strong>{formatarMoeda(dados.valor)}</strong>
              </div>
            )}

            {status === 'Pendente' && dados.brcode && (
              <>
                <div className="pix-qr-cli">
                  <QRCodeSVG size={220} value={dados.brcode} includeMargin />
                </div>
                <p className="pix-qr-dica-cli">
                  <i className="bi bi-qr-code-scan" /> Abra o app do seu banco e escaneie o QR Code
                </p>
                <button
                  type="button"
                  className="pix-btn-copiar-cli"
                  onClick={copiarChave}
                >
                  <i className="bi bi-clipboard-check" /> Copiar chave PIX
                </button>
                <p className="pix-ou-cli">— ou copie a chave manualmente —</p>
                <button
                  type="button"
                  className="pix-chave-cli"
                  onClick={copiarChave}
                  title="Toque para copiar"
                >
                  {dados.brcode}
                </button>
              </>
            )}

            {expiracaoTexto && status === 'Pendente' && (
              <small className="pix-expiracao-cli">
                <i className="bi bi-clock" /> {expiracaoTexto}
              </small>
            )}

            <div className="pix-info-pedido-cli">
              {dados.numped && (
                <div><span>Pedido</span><strong>{dados.numped}</strong></div>
              )}
              {dados.nomeVendedor && (
                <div><span>Vendedor</span><strong>{dados.nomeVendedor}</strong></div>
              )}
              {dados.nomeCliente && (
                <div><span>Cliente</span><strong>{dados.nomeCliente}</strong></div>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="pix-footer-cli">
        <span>© Centrão Materiais de Construção</span>
        <span className="pix-footer-secundario-cli">Pagamento seguro via PIX.</span>
      </footer>
    </div>
  );
}
